import {
  onCleanup,
  batch,
  createRoot,
  createEffect,
  onMount,
  createSignal,
  createMemo,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { createStore } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/store/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import {
  register,
  createContext,
  withProvider,
  withConsumer,
  compose,
} from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';
import { animate } from 'https://cdn.jsdelivr.net/npm/motion@10.16.2/+esm';

// Utility custom element functions

function getContext(element) {
  const elementContextSymbol = Object.getOwnPropertySymbols(element).find((s) => s.description === 'element-context');
  const contextSymbol = Object.getOwnPropertySymbols(element[elementContextSymbol]).find(
    (s) => s.description === 'context'
  );
  return element[elementContextSymbol][contextSymbol];
}

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

// Components
function TabBlock(props, { element }) {
  const [state, setState] = getContext(element);

  createEffect(() => {
    batch(() => {
      element.setAttribute('active-tab', state.activeTab);
      element.setAttribute('is-animating', state.isAnimating);
      setState('activeTab', props.activeTab);
      setState('transitionDuration', props.transitionDuration);
      setState('mode', props.mode);
    });
  });
  createEffect(() => {
    if (!state.animationQueue.length) return;
    const animations = Promise.all([...state.animationQueue]);
    setState('isAnimating', true);
    animations.then(() => {
      batch(() => {
        setState('isAnimating', false);
        setState('animationQueue', []);
      });
    });
  });
}

function TabTrigger(props, { element, tab }) {
  const [state, setState] = tab;
  const root = element.closest('tab-block');

  if (!state.activeTab) {
    const tab = root.querySelector(props.target);

    if (tab && tab.hasAttribute('is-active')) {
      return element.setAttribute('is-active', true);
    }
  }

  createEffect(() => {
    element.setAttribute('is-active', state.activeTab === props.target);
  });

  onMount(() => {
    const handleClick = (e) => {
      e.preventDefault();
      if (state.isAnimating) return;
      return setState('activeTab', props.target);
    };

    element.addEventListener('click', handleClick);
    onCleanup(() => {
      element.removeEventListener('click', handleClick);
    });
  });
}

function TabPanel(props, { element, tab }) {
  const [state, setState] = tab;

  const hideElement = (element) => {
    element.style.cssText = `
      position: absolute;
      display: none;
      opacity: 0;
      visibility: hidden;
    `;
  };
  const showElement = (element) => {
    element.style.cssText = `
      position: relative;
      display: block;
      opacity: 0;
      visibility: visible;
    `;
  };
  const transition = createMemo(() => ({ duration: state.transitionDuration }));

  const fadeIn = (element) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        showElement(element);
        resolve(animate(element, { opacity: [0, 1] }, transition()).finished);
      }, transition().duration * 1000);
    });
  };

  const fadeOut = (element) => {
    const animation = animate(element, { opacity: [1, 0] }, transition());
    return animation.finished.then(() => {
      hideElement(element);
      return animation.finished;
    });
  };

  createEffect(() => {
    const isActive = element.matches(state.activeTab);
    batch(() => {
      element.setAttribute('aria-hidden', !isActive);
      element.setAttribute('is-active', isActive);
    });
  });

  createEffect(() => {
    const isActive = element.matches(state.activeTab);

    if (isActive) return;

    const isFirstRender = !element.style.opacity;
    if (isFirstRender) {
      return (element.style.cssText = `
        display: none;
        opacity: 0;
        position: absolute;
        visibility: hidden;
      `);
    }

    setState('animationQueue', (state) => [...state, fadeOut(element)]);
  });

  createEffect(() => {
    const isActive = element.matches(state.activeTab);

    if (!isActive) return;

    const isFirstRender = !element.style.opacity;
    if (isFirstRender) {
      return (element.style.cssText = `
        display: block;
        opacity: 1;
        position: relative;
        visibility: visible;
      `);
    }

    setState('animationQueue', (state) => [...state, fadeIn(element)]);
  });
}

// Context
const TabContext = createContext(
  (state = { isAnimating: false, activeTab: '', mode: 'wait', transitionDuration: 0.35, animationQueue: [] }) => {
    return createStore(state);
  }
);

// Custom Element Definitions
customShadowlessElement(
  'tab-block',
  { activeTab: '', isAnimating: false, transitionDuration: 0.35, mode: 'wait' },
  TabBlock,
  withProvider(TabContext)
);

customShadowlessElement('tab-trigger', { isActive: false, target: '' }, TabTrigger, withConsumer(TabContext, 'tab'));

customShadowlessElement(
  'tab-panel',
  { isActive: false, ariaHidden: true, tabindex: 0 },
  TabPanel,
  withConsumer(TabContext, 'tab')
);
